/**
 * audio-to-notes-clean.js — note post-processing + engine-routing helpers
 * for the audio-to-notes tool. The single biggest accuracy problem (prod
 * telemetry, 30 d): Basic Pitch output is used RAW at ~6.6 notes/sec avg
 * (max 4092 / file) — sustained melody notes shattered by vibrato, breath,
 * harmonics and re-onsets into a fragment soup. That reads to users as
 * "совсем не те ноты и ритм". A melodic line is ~1–3 notes/sec.
 *
 * This module is pure-functional (no DOM, no model) so it's unit-testable
 * and engine-agnostic — it cleans whatever note array it's given, whether
 * from Basic Pitch (polyphonic CNN) or from the YIN PitchTracker
 * (monophonic, used for solo/vocal input).
 *
 * Exposed on window.__atnClean:
 *   polyphony(notes)            → avg simultaneous voices over the active
 *                                 span (≈1 ⇒ monophonic, >1.5 ⇒ chordal)
 *   cleanNotes(notes, opts)     → de-fragment + filter + optional mono-collapse
 *   yinSegmentsToNotes(segs,o)  → PitchTracker segments → cleaned notes
 *   extractMelody(notes, opts)  → collapse a polyphonic note set to one
 *                                 readable lead-melody line (multi-hypothesis
 *                                 salience + pitch-continuity Viterbi)
 *
 * Style: var/function to match audio-to-notes-renderer.js + -chords.js.
 */
(function(){
    'use strict';

    function midiFromHz(hz){ return Math.round(69 + 12 * Math.log2(hz / 440)); }

    // Median absolute inter-note interval (semitones) of a note line taken in
    // time order. A coherent sung/played melody steps mostly by ≤2–3 semitones;
    // the monophonic YIN tracker chasing whatever partial is loudest across a
    // full polyphonic MIX jumps by a fifth or more between most notes. Measured
    // on real audio: a solo vocal's YIN line has median 2, a 4-minute song mix's
    // YIN line median 7. So this cleanly says whether a YIN result is a real
    // single voice or garbage tracked off a band — the auto-arbitration uses it
    // to stop routing polyphonic songs to YIN (see audio-to-notes.blade.php).
    // Returns 0 for <3 notes (not enough to judge — never triggers a rejection).
    function melodicMedianJump(notes){
        if (!notes || notes.length < 3) return 0;
        var arr = notes.slice().sort(function(a, b){ return a.startTimeSeconds - b.startTimeSeconds; });
        var d = [];
        for (var i = 1; i < arr.length; i++) d.push(Math.abs(arr[i].pitchMidi - arr[i - 1].pitchMidi));
        d.sort(function(a, b){ return a - b; });
        return d[d.length >> 1];
    }

    // ── Is the line we are SHOWING plausibly the main voice? ───────────
    // How far the displayed line sits BELOW the upper register of everything we
    // detected. In almost all music the leading voice is at or near the top, so
    // a line well under the top of the material is a register mistake — and it is
    // the mistake the coherence check is blind to, because an octave-displaced
    // line is perfectly coherent.
    //
    // ⭐ Measured against LABELLED benchmarks (2026-08-13), not chosen. Two sets,
    // deliberately different in domain and metric, one selecting and one
    // validating:
    //   • Orchset (64 symphonic excerpts, the melody annotated as "what a
    //     listener would hum") scored by Raw Pitch Accuracy;
    //   • GuitarSet (CC BY 4.0, solo guitar, note-level truth from a hexaphonic
    //     pickup) scored by note-level F1.
    // Nine candidate "we are probably wrong" signals were ranked by AUC against
    // ground-truth accuracy. This one plus the existing median jump wins on the
    // WORST of the two sets — the only ranking that cannot be gamed by a signal
    // that happens to suit one domain:
    //   belowTop + medianJump   0.751 / 0.970   ← chosen
    //   belowTop alone          0.742 / 0.950
    //   notes per second        0.665 / 0.925
    //   melody share            0.615 / 0.950
    //   median jump alone       0.595 / 0.815   ← what we shipped before
    //   average polyphony       0.552 / 0.975   ← best on one set, useless on the
    //                                             other; exactly the trap the
    //                                             cross-check exists to catch
    // Full numbers: docs/audio-to-notes-ground-truth-2026-08-13.md.
    // ⚠️ Defined ONLY when the displayed line is a proper subset of the material.
    // If we are showing everything, there is no "rest of the material" to sit
    // below, and comparing the set against itself just measures its own pitch
    // spread — which says nothing about picking the wrong voice. Shipping without
    // this guard made the notice fire on the solo-vocal etalon (a monophonic YIN
    // line displayed in full: p90 minus median of the SAME notes ≈ 13), and the
    // etalon caught it. Require a quarter of the material to be hidden before the
    // question "is the shown line too low?" even has a subject.
    var GAP_MIN_HIDDEN = 0.25;
    function lineRegisterGap(shownNotes, allNotes){
        if (!shownNotes || !shownNotes.length || !allNotes || !allNotes.length) return 0;
        if (shownNotes === allNotes) return 0;
        if (shownNotes.length > allNotes.length * (1 - GAP_MIN_HIDDEN)) return 0;
        var tops = [], i;
        for (i = 0; i < allNotes.length; i++) tops.push(allNotes[i].pitchMidi);
        tops.sort(function(a, b){ return a - b; });
        // 90th percentile, not the max: one stray high detection must not decide.
        var top = tops[Math.floor(tops.length * 0.9)];
        var shown = [];
        for (i = 0; i < shownNotes.length; i++) shown.push(shownNotes[i].pitchMidi);
        shown.sort(function(a, b){ return a - b; });
        return Math.max(0, top - shown[shown.length >> 1]);
    }

    // ── Did the monophonic tracker drop real voices? ───────────────────
    // The question the YIN-vs-BP arbitration actually has to answer is "was the
    // SOURCE one voice?", and that cannot be read off YIN's output, because
    // YIN's output is monophonic BY CONSTRUCTION. Every gate tried so far was a
    // property of that output — first its polyphony (vacuous: always ≈1.0), then
    // the coherence of its line — so none of them could detect the failure that
    // matters: YIN winning on real polyphony and silently throwing the other
    // voices away. Measured 2026-08-13 on real CC0 solo-piano recordings against
    // the score MIDI of the same pieces: it happened on 2 of 5, one of them a
    // FOUR-VOICE FUGUE, and on the fast variation YIN returned 4.6 notes/s where
    // the score has 11.7. See docs/audio-to-notes-ground-truth-2026-08-13.md.
    //
    // So compare the two candidate answers on the same audio, using the one
    // asymmetry that is PHYSICS rather than style:
    //
    //   a partial of a tone is always ABOVE it — a harmonic below the
    //   fundamental does not exist.
    //
    // Therefore a note the polyphonic model reports sounding WHILE the tracked
    // line sounds but BELOW it cannot be an artifact of that line. It is another
    // voice (a left hand, a bass) or noise. Notes above the line are ambiguous
    // (they may be partials), so they are deliberately not counted.
    //
    // ⚠️ Chosen only after five cheaper candidates were measured and REJECTED —
    // do not swap one back in without re-measuring:
    //   • amplitude ("the ghost is quieter"): BP's amplitude spans 0.33–0.47 and
    //     the ghost/parent loudness ratio has median 0.89–1.00. No dynamic range,
    //     no discrimination.
    //   • note density: real piano runs 2.5–11.7 notes/s, straight through every
    //     threshold we had.
    //   • average polyphony: real piano 1.8–2.9 overlaps mixes 1.8–3.3.
    //   • interval content (share of 3rds/6ths = "counterpoint"): real piano
    //     32–50 %, but a solo VOICE measured 32 % and 36 % — same band.
    //   • THE MASS SHARE OF THE SAME BELOW-LINE CONTENT — i.e. this very metric
    //     divided by co-sounding SECONDS instead of by presence over TIME. It
    //     looked convincing on 45-second excerpts (voice 1.9 %, piano 27 %),
    //     shipped, and the vocal etalon caught it on the full file: voice 25.6 %
    //     vs piano 27.6 %, no separation at all. The denominator was the bug —
    //     co-sounding time is bounded by how long YIN decides the line is voiced
    //     (measured: 45 % of the span on that file), so the ratio moved with the
    //     tracker's voicing decisions rather than with the music.
    //
    // Hence PRESENCE OVER TIME: of the tracked line's span, what share of it has
    // any independent below-line content at all. A real left hand is there
    // throughout; stem bleed and model glitches are episodic. Measured, excerpt
    // AND full length: one voice 2.4 % / 11.9 %, solo piano 68.2 % / 73.3 % /
    // 72.4 % — a 6× gap that does not move with file length.
    //
    // `tolSemis` guards against tracker error around the line itself;
    // `bucketSec` is the presence resolution. Returns -1 when there is no line
    // span to measure over.
    function voicesBelowLine(polyNotes, lineNotes, opts){
        return voicesOffLine(polyNotes, lineNotes, 'below', opts);
    }
    // The same presence measure for content ABOVE the line — the voice a one-voice
    // tracker drops when it latches onto the LOWER of two singers. Measured
    // 2026-10-18 (Choral Singing Dataset, CC BY 4.0): on a two-part treble choir
    // YIN followed the alto 70–77 % of the time, the soprano ~30 %, and won the
    // arbitration — `voicesBelowLine` could not object, the lost voice was above.
    // Above the line sit the line's own partials, so intervals at the 2nd–5th
    // harmonic (12, 19, 24, 28 semitones ± 1) do not count: what is left cannot be
    // a partial of the line. One voice (40 a cappella vocadito singers, 12 single
    // choir singers, 3 vocal stems): at most 0.208, median 0.05; two-part choirs
    // 0.508–0.662 — the same 0.30 cut as below sits between the two bands.
    var HARMONIC_SEMIS = [12, 19, 24, 28];
    function voicesAboveLine(polyNotes, lineNotes, opts){
        return voicesOffLine(polyNotes, lineNotes, 'above', opts);
    }
    function voicesOffLine(polyNotes, lineNotes, side, opts){
        opts = opts || {};
        var tol = opts.tolSemis != null ? opts.tolSemis : 2;
        var bucket = opts.bucketSec != null ? opts.bucketSec : 0.25;
        if (!polyNotes || !polyNotes.length || !lineNotes || !lineNotes.length) return -1;
        var line = lineNotes.slice().sort(function(a, b){ return a.startTimeSeconds - b.startTimeSeconds; });
        var poly = polyNotes.slice().sort(function(a, b){ return a.startTimeSeconds - b.startTimeSeconds; });
        var t0 = Infinity, t1 = -Infinity, k;
        for (k = 0; k < line.length; k++) {
            var ls = line[k].startTimeSeconds, le = ls + (line[k].durationSeconds || 0);
            if (ls < t0) t0 = ls;
            if (le > t1) t1 = le;
        }
        if (!(t1 > t0)) return -1;
        var marked = {}, markedCount = 0, from = 0;
        for (var i = 0; i < poly.length; i++) {
            var n = poly[i], ns = n.startTimeSeconds, ne = ns + (n.durationSeconds || 0);
            if (!(ne > ns)) continue;
            // Advance past line notes that ended before n began. poly is sorted,
            // so `from` only moves forward across the whole loop.
            while (from < line.length && line[from].startTimeSeconds + (line[from].durationSeconds || 0) <= ns) from++;
            for (var j = from; j < line.length; j++) {
                var y = line[j], ys = y.startTimeSeconds, ye = ys + (y.durationSeconds || 0);
                if (ys >= ne) break;
                var os = Math.max(ns, ys), oe = Math.min(ne, ye);
                if (oe <= os) continue;
                if (side === 'above') {
                    var iv = n.pitchMidi - y.pitchMidi;
                    if (iv <= tol) continue;
                    var partial = false;
                    for (var h = 0; h < HARMONIC_SEMIS.length; h++) if (Math.abs(iv - HARMONIC_SEMIS[h]) <= 1) { partial = true; break; }
                    if (partial) continue;
                } else if (n.pitchMidi >= y.pitchMidi - tol) continue;
                var a = Math.floor(os / bucket), b = Math.floor(oe / bucket);
                for (var q = a; q <= b; q++) {
                    if (!marked[q]) { marked[q] = 1; markedCount++; }
                }
            }
        }
        return markedCount / Math.max(1, Math.ceil((t1 - t0) / bucket));
    }

    // Average simultaneous voices = (sum of note durations) / (union span of
    // all notes). Monophonic input lands near 1.0; dense chords run 2–4.
    function polyphony(notes){
        if (!notes || !notes.length) return 0;
        var total = 0, iv = [];
        for (var i = 0; i < notes.length; i++) {
            var d = notes[i].durationSeconds || 0;
            total += d;
            iv.push([notes[i].startTimeSeconds, notes[i].startTimeSeconds + d]);
        }
        iv.sort(function(a, b){ return a[0] - b[0]; });
        var covered = 0, s = iv[0][0], e = iv[0][1];
        for (var k = 1; k < iv.length; k++) {
            if (iv[k][0] <= e) { if (iv[k][1] > e) e = iv[k][1]; }
            else { covered += e - s; s = iv[k][0]; e = iv[k][1]; }
        }
        covered += e - s;
        return covered > 0 ? total / covered : 0;
    }

    // Merge / filter / (optional) monophonic-collapse.
    //   minDurSec    drop fragments shorter than this (default 60 ms)
    //   mergeGapSec  merge consecutive SAME-pitch notes within this gap —
    //                kills vibrato / re-onset shatter (default 120 ms)
    //   minAmpFrac   drop notes weaker than this × median amplitude
    //                (default 0.18 — removes harmonic-ghost crumbs without
    //                eating genuinely quiet notes)
    //   monophonic   keep one note at a time (loudest×longest wins) —
    //                strips octave/fifth ghosts from a poly model on a
    //                solo source. Use only when the input is monophonic.
    function cleanNotes(notes, opts){
        opts = opts || {};
        var minDur = opts.minDurSec != null ? opts.minDurSec : 0.06;
        // 50 ms: fuses Basic Pitch's sub-tone splits (a continuous tone the
        // CNN broke on a vibrato amplitude dip — gaps of 1–4 frames ≈ 12–46 ms)
        // while preserving intentionally repeated notes (which carry a real
        // articulation gap ≥ ~½ beat). Onset info is gone by this stage, so
        // gap is the only discriminator — err toward preserving rhythm.
        var mergeGap = opts.mergeGapSec != null ? opts.mergeGapSec : 0.05;
        var minAmpFrac = opts.minAmpFrac != null ? opts.minAmpFrac : 0.18;
        var mono = !!opts.monophonic;
        if (!notes || !notes.length) return [];

        // ── 1. De-fragment: per exact pitch, merge notes whose inter-onset
        //       gap is below mergeGap into one sustained note. ──
        var byPitch = {};
        for (var i = 0; i < notes.length; i++) {
            var p = notes[i].pitchMidi;
            (byPitch[p] = byPitch[p] || []).push(notes[i]);
        }
        var merged = [];
        var keys = Object.keys(byPitch);
        for (var ki = 0; ki < keys.length; ki++) {
            var arr = byPitch[keys[ki]].slice().sort(function(a, b){ return a.startTimeSeconds - b.startTimeSeconds; });
            var cur = null;
            for (var a = 0; a < arr.length; a++) {
                var n = arr[a];
                if (cur && (n.startTimeSeconds - (cur.startTimeSeconds + cur.durationSeconds)) <= mergeGap) {
                    var end = Math.max(cur.startTimeSeconds + cur.durationSeconds, n.startTimeSeconds + n.durationSeconds);
                    cur.durationSeconds = end - cur.startTimeSeconds;
                    if ((n.amplitude || 0) > (cur.amplitude || 0)) cur.amplitude = n.amplitude;
                } else {
                    cur = { pitchMidi: +keys[ki], startTimeSeconds: n.startTimeSeconds,
                            durationSeconds: n.durationSeconds, amplitude: n.amplitude || 0.5 };
                    merged.push(cur);
                }
            }
        }

        // ── 2. Amplitude floor (relative to median) + minimum duration. ──
        var amps = merged.map(function(n){ return n.amplitude || 0; }).sort(function(a, b){ return a - b; });
        var medAmp = amps.length ? amps[amps.length >> 1] : 0;
        var ampFloor = medAmp * minAmpFrac;
        merged = merged.filter(function(n){
            return n.durationSeconds >= minDur && (n.amplitude || 0) >= ampFloor;
        });

        // ── 3. Monophonic collapse: greedily keep the strongest notes that
        //       don't substantially overlap an already-kept note. Removes
        //       harmonic ghosts (octave/fifth) that sit above the melody. ──
        if (mono && merged.length) {
            var byStrength = merged.slice().sort(function(a, b){
                return (b.amplitude * b.durationSeconds) - (a.amplitude * a.durationSeconds);
            });
            var kept = [];
            for (var c = 0; c < byStrength.length; c++) {
                var cand = byStrength[c];
                var cs = cand.startTimeSeconds, ce = cs + cand.durationSeconds, clash = false;
                for (var j = 0; j < kept.length; j++) {
                    var kp = kept[j], ks = kp.startTimeSeconds, ke = ks + kp.durationSeconds;
                    var ov = Math.min(ce, ke) - Math.max(cs, ks);
                    if (ov > 0.5 * Math.min(cand.durationSeconds, kp.durationSeconds)) { clash = true; break; }
                }
                if (!clash) kept.push(cand);
            }
            merged = kept;
        }

        merged.sort(function(a, b){
            return a.startTimeSeconds - b.startTimeSeconds || a.pitchMidi - b.pitchMidi;
        });
        return merged;
    }

    // ── Vocal-tuned frame segmentation ────────────────────────────────
    // PitchTracker's built-in segmenter walks RAW frames with a ±50-cent
    // jump gate — perfect for steady instrument tones, but real singing
    // has vibrato of ±50–150 cents at 5–6 Hz, so sustained vocal notes get
    // shredded into <90 ms fragments that the minSegment gate then drops.
    // Measured on a real separated vocal stem (Zvjozdochka, 2026-07-16,
    // vs a librosa-pyin reference): the raw-frame segmenter kept 20% of
    // the sung material; this smoothed segmenter keeps 41% at ~9% false
    // activation — with the tracker run at cmndThreshold 0.45 + a LOW
    // minConf so ALL frames (with confidences) reach us and the voicing
    // decision happens here via hysteresis.
    //
    // Steps: (1) median-smooth the semitone track over `medianK` frames —
    // vibrato averages out, glides survive; (2) confidence HYSTERESIS —
    // a segment must START at ≥enterConf but only needs ≥sustainConf to
    // continue (soft note tails / breathy onsets stop cutting notes off);
    // (3) split on >jumpSemi jumps of the smoothed track vs the running
    // median; (4) octave-continuity: an isolated segment ±1–2 octaves off
    // its two neighbors (which agree with each other) is a tracker
    // sub/super-harmonic lock — transpose it back into the line.
    // Returns segments in PitchTracker's shape, feed to yinSegmentsToNotes.
    function segmentVocalFrames(frames, opts){
        opts = opts || {};
        var enterConf  = opts.enterConf  != null ? opts.enterConf  : 0.70;
        var sustainConf= opts.sustainConf!= null ? opts.sustainConf: 0.50;
        var medianK    = opts.medianK    != null ? opts.medianK    : 9;
        var jumpSemi   = opts.jumpSemi   != null ? opts.jumpSemi   : 0.8;
        var minSegMs   = opts.minSegMs   != null ? opts.minSegMs   : 80;
        var gapBreakMs = opts.gapBreakMs != null ? opts.gapBreakMs : 120;
        if (!frames || !frames.length) return [];
        var n = frames.length;
        var midi = new Array(n);
        for (var i0 = 0; i0 < n; i0++) {
            midi[i0] = (frames[i0].freqHz > 0) ? 69 + 12 * Math.log2(frames[i0].freqHz / 440) : null;
        }
        var half = medianK >> 1;
        var msm = new Array(n);
        for (var i1 = 0; i1 < n; i1++) {
            var lo = Math.max(0, i1 - half), hi = Math.min(n, i1 + half + 1), w = [];
            for (var j = lo; j < hi; j++) if (midi[j] != null) w.push(midi[j]);
            if (w.length >= 2) { w.sort(function(a, b){ return a - b; }); msm[i1] = w[w.length >> 1]; }
            else msm[i1] = null;
        }
        var segs = [], cur = null, lastT = null;
        function median(arr){ var s = arr.slice().sort(function(a, b){ return a - b; }); return s[s.length >> 1]; }
        function flush(endT){
            if (!cur) return;
            if (endT - cur.start >= minSegMs / 1000 && cur.vals.length >= 4) {
                segs.push({ startS: cur.start, endS: endT, medianMidi: median(cur.vals), frames: cur.vals.length });
            }
            cur = null;
        }
        for (var i2 = 0; i2 < n; i2++) {
            var t = frames[i2].timeS, v = msm[i2], conf = frames[i2].confidence || 0;
            var voiced = (v != null) && conf >= (cur ? sustainConf : enterConf);
            if (!voiced) {
                if (cur && lastT != null && (t - lastT) > gapBreakMs / 1000) flush(lastT);
                continue;
            }
            if (!cur) { cur = { start: t, vals: [v] }; lastT = t; continue; }
            if (Math.abs(v - median(cur.vals)) > jumpSemi) { flush(lastT); cur = { start: t, vals: [v] }; }
            else cur.vals.push(v);
            lastT = t;
        }
        flush(lastT);
        // (4) octave-continuity repair between agreeing neighbors.
        for (var k = 1; k < segs.length - 1; k++) {
            var prev = segs[k - 1].medianMidi, mid = segs[k].medianMidi, next = segs[k + 1].medianMidi;
            if (Math.abs(prev - next) > 3) continue;
            var center = (prev + next) / 2;
            if (Math.abs(mid - center) <= 6) continue;
            var oct = [12, 24, -12, -24];
            for (var oi = 0; oi < oct.length; oi++) {
                if (Math.abs(mid - oct[oi] - center) <= 3) { segs[k].medianMidi = mid - oct[oi]; break; }
            }
        }
        // PitchTracker shape (medianFreqHz) for yinSegmentsToNotes.
        for (var m2 = 0; m2 < segs.length; m2++) {
            segs[m2].medianFreqHz = 440 * Math.pow(2, (segs[m2].medianMidi - 69) / 12);
            delete segs[m2].medianMidi;
        }
        return segs;
    }

    // Frames from a NEURAL pitch tracker → note segments.
    //
    // segmentVocalFrames was built for YIN, whose confidence says little about
    // syllables, so it splits a sung line only on a pitch jump or a voicing gap.
    // Measured on vocadito (40 solo voices, two human note annotations): a new
    // syllable on the SAME pitch is found 14% of the time, and 28% of all sung
    // notes are shorter than 120 ms. A neural tracker's voicing evidence does
    // mark the syllable: at a same-pitch boundary it drops by 0.19–0.37 (median)
    // against 0.003–0.05 inside a note — AUC 0.86 (RMVPE) … 0.90 (CREPE). So
    // this segmenter also cuts at a confidence DIP, which is what a lead sheet
    // needs: one syllable of the words, one note.
    //
    // A dip is a frame whose confidence is the lowest within ±dipWinMs/2 and
    // sits dipDepth below the lower of the two peaks around it. A split leaves a
    // one-frame gap so the same-pitch merge downstream cannot glue it back.
    function segmentNeuralFrames(frames, opts){
        // Defaults = the configuration chosen on vocadito's even-numbered voices
        // (clean + over a band at 6 dB) and CONFIRMED on the untouched odd half,
        // the 0/12 dB mixes, LizNelson and 76 MIR-ST500 pop songs (2026-09-23,
        // a2nbench/tune_seg.py). enter/sustain 0.5/0.3 over the looser 0.3/0.2:
        // equal note F1, but false notes in instrumental breaks drop from 21–32 %
        // to 9–11 % of frames on the real song.
        opts = opts || {};
        var enterConf   = opts.enterConf   != null ? opts.enterConf   : 0.5;
        var sustainConf = opts.sustainConf != null ? opts.sustainConf : 0.3;
        var medianK     = opts.medianK     != null ? opts.medianK     : 3;
        var jumpSemi    = opts.jumpSemi    != null ? opts.jumpSemi    : 1.3;
        var minSegMs    = opts.minSegMs    != null ? opts.minSegMs    : 60;
        var gapBreakMs  = opts.gapBreakMs  != null ? opts.gapBreakMs  : 20;
        var dipDepth    = opts.dipDepth    != null ? opts.dipDepth    : 0.3;
        var dipWinMs    = opts.dipWinMs    != null ? opts.dipWinMs    : 150;
        if (!frames || frames.length < 3) return [];
        var n = frames.length;
        var hop = (frames[n - 1].timeS - frames[0].timeS) / (n - 1) || 0.01;
        var midi = new Array(n), conf = new Array(n);
        for (var i0 = 0; i0 < n; i0++) {
            var f = frames[i0].freqHz;
            midi[i0] = (f > 0) ? 69 + 12 * Math.log2(f / 440) : null;
            conf[i0] = frames[i0].confidence || 0;
        }
        var half = medianK >> 1, msm = new Array(n);
        for (var i1 = 0; i1 < n; i1++) {
            var lo = Math.max(0, i1 - half), hi = Math.min(n, i1 + half + 1), w = [];
            for (var j = lo; j < hi; j++) if (midi[j] != null && conf[j] >= sustainConf) w.push(midi[j]);
            if (w.length) { w.sort(function(a, b){ return a - b; }); msm[i1] = w[w.length >> 1]; }
            else msm[i1] = midi[i1];
        }
        // Dips: local minimum over the window, deep relative to both flanks.
        var dw = Math.max(1, Math.round(dipWinMs / 1000 / hop / 2));
        var dip = new Array(n);
        for (var i2 = 0; i2 < n; i2++) {
            dip[i2] = false;
            if (dipDepth <= 0) continue;
            var c = conf[i2], isMin = true, lPeak = 0, rPeak = 0;
            for (var k = Math.max(0, i2 - dw); k <= Math.min(n - 1, i2 + dw); k++) {
                if (conf[k] < c || (conf[k] === c && k < i2)) { isMin = false; break; }
                if (k < i2 && conf[k] > lPeak) lPeak = conf[k];
                if (k > i2 && conf[k] > rPeak) rPeak = conf[k];
            }
            if (isMin && Math.min(lPeak, rPeak) - c >= dipDepth) dip[i2] = true;
        }
        var segs = [], cur = null, lastT = null;
        function median(arr){ var s = arr.slice().sort(function(a, b){ return a - b; }); return s[s.length >> 1]; }
        function flush(endT){
            if (!cur) return;
            if (endT - cur.start >= minSegMs / 1000 && cur.vals.length >= 3) {
                segs.push({ startS: cur.start, endS: endT, medianMidi: median(cur.vals), frames: cur.vals.length });
            }
            cur = null;
        }
        for (var i3 = 0; i3 < n; i3++) {
            var t = frames[i3].timeS, v = msm[i3];
            var voiced = (v != null) && conf[i3] >= (cur ? sustainConf : enterConf);
            if (!voiced) {
                if (cur && lastT != null && (t - lastT) > gapBreakMs / 1000) flush(lastT);
                continue;
            }
            if (cur && dip[i3]) { flush(lastT); lastT = t; continue; }   // the dip frame itself is the gap
            if (!cur) { cur = { start: t, vals: [v] }; lastT = t; continue; }
            if (Math.abs(v - median(cur.vals)) > jumpSemi) { flush(lastT); cur = { start: t, vals: [v] }; }
            else cur.vals.push(v);
            lastT = t;
        }
        flush(lastT);
        for (var q = 1; q < segs.length - 1; q++) {
            var prev = segs[q - 1].medianMidi, mid = segs[q].medianMidi, next = segs[q + 1].medianMidi;
            if (Math.abs(prev - next) > 3) continue;
            var center = (prev + next) / 2;
            if (Math.abs(mid - center) <= 6) continue;
            var oct = [12, 24, -12, -24];
            for (var oi = 0; oi < oct.length; oi++) {
                if (Math.abs(mid - oct[oi] - center) <= 3) { segs[q].medianMidi = mid - oct[oi]; break; }
            }
        }
        for (var m2 = 0; m2 < segs.length; m2++) {
            segs[m2].medianFreqHz = 440 * Math.pow(2, (segs[m2].medianMidi - 69) / 12);
            delete segs[m2].medianMidi;
        }
        return segs;
    }

    // Neural frames → notes, with the note clean-up the dip splits need: a
    // same-pitch merge wider than the one-frame gap a dip leaves would glue the
    // syllables straight back together (measured: mergeGap 0.03+ undid them).
    function neuralFramesToNotes(frames, opts){
        return yinSegmentsToNotes(segmentNeuralFrames(frames, opts), { mergeGapSec: 0.01, minDurSec: 0.07 });
    }

    // PitchTracker.track() segments → note objects, then cleaned (same-pitch
    // merge consolidates re-articulated notes the ±50-cent segmenter split).
    function yinSegmentsToNotes(segments, opts){
        if (!segments || !segments.length) return [];
        var notes = [];
        for (var i = 0; i < segments.length; i++) {
            var s = segments[i];
            if (!(s.medianFreqHz > 0)) continue;
            var midi = midiFromHz(s.medianFreqHz);
            if (!isFinite(midi) || midi < 21 || midi > 108) continue;
            notes.push({ pitchMidi: midi, startTimeSeconds: s.startS,
                         durationSeconds: Math.max(0.04, s.endS - s.startS),
                         amplitude: 0.8 });
        }
        // YIN is already monophonic, so no mono-collapse — just merge/filter.
        return cleanNotes(notes, { minDurSec: (opts && opts.minDurSec) || 0.07,
                                   mergeGapSec: (opts && opts.mergeGapSec) || 0.12,
                                   minAmpFrac: 0, monophonic: false });
    }

    // ── Predominant-melody extraction ─────────────────────────────────
    // Collapse a polyphonic note set (Basic Pitch on a full-song mix) into a
    // single readable melodic line. On a full mix Basic Pitch faithfully
    // transcribes EVERY simultaneous pitch → 5–15 notes/sec of overlapping
    // accompaniment + octave/harmonic ghosts that reads to users as "не
    // переводит в ноты" (prod 2026-06: 75% of BP results ≥4 nps, 39% ≥6 nps
    // "soup"; one 8 MB / 365 s song → 1897 notes). What a human means by
    // "the notes of the song" is the lead melody — one note at a time.
    // Source separation would be cleaner but is a licensing dead-end (every
    // quality stem model is MUSDB-tainted); this is pure DSP on Basic
    // Pitch's commercially-clean (Apache-2.0) output.
    //
    // Algorithm: MULTI-HYPOTHESIS frame-based salience + pitch-continuity
    // Viterbi. The melody's register varies per song (female vs male vocal,
    // bass-register lead, etc.), and a single point-estimate of that register
    // is fragile when a dense accompaniment cluster accumulates as much
    // amplitude as the lead. So instead of guessing the centre once, we decode
    // the line at a GRID of candidate centres and keep the most melody-like one:
    //   • a hop grid (default 50 ms) over the active span; per frame the
    //     candidates are the notes sounding then
    //   • for each candidate register centre, run a Viterbi whose emission is
    //     1 − (amplitude × Gaussian register prior) and whose transition is
    //     jumpCost·|Δsemitone| — so the path tracks ONE coherent voice instead
    //     of hopping between accompaniment notes; a REST state lets the line
    //     breathe between phrases instead of inventing filler
    //   • score each decoded line by salient-coverage × activity^P: the melody
    //     is both LOUD/present AND active (many moving notes), which beats a
    //     quiet pad, a loud-but-static sustained counter-line, and a fragmented
    //     accompaniment run. The winning centre self-selects from the data.
    //   • decode → re-segment equal runs → cleanNotes
    //
    // Validated on real Basic Pitch output (Herd, 2026-06): a 4.6-nps mid-
    // register melody mix → 1.3 nps, 100% melody recall, 100% precision; a
    // 5.1-nps dense LOW-melody mix with a sustained high counter-line trap →
    // 1.5 nps, recall = Basic Pitch's own detection ceiling, 90% precision.
    // The naive mono-collapse only recovered ~21%.

    // One Viterbi pass at a fixed register centre → decoded note line.
    function viterbiMelody(cand, nF, hop, t0, center, sigma, jumpCost, restCost, restTrans, minDur){
        function regWeight(p){ var d = (p - center) / sigma; return Math.exp(-0.5 * d * d); }
        var prev = null, trellis = new Array(nF);
        for (var f = 0; f < nF; f++) {
            var list = cand[f], maxAmp = 0, sal = {};
            for (var c = 0; c < list.length; c++) { var a = list[c].amplitude || 0.5; if (a > maxAmp) maxAmp = a; }
            maxAmp = maxAmp || 1;
            for (var c2 = 0; c2 < list.length; c2++) {
                var p = list[c2].pitchMidi;
                var sv = (list[c2].amplitude || 0.5) / maxAmp * regWeight(p);
                if (sal[p] == null || sv > sal[p]) sal[p] = sv;
            }
            var states = [], pitches = Object.keys(sal);
            for (var pi = 0; pi < pitches.length; pi++) states.push({ pitch: +pitches[pi], emit: 1 - sal[pitches[pi]] });
            states.push({ pitch: null, emit: restCost });

            var cur = [], back = [];
            for (var si = 0; si < states.length; si++) {
                var st = states[si], bestCost = Infinity, bestBack = -1;
                if (prev == null) { bestCost = 0; bestBack = -1; }
                else {
                    for (var qi = 0; qi < prev.length; qi++) {
                        var q = prev[qi], tc;
                        if (q.pitch == null && st.pitch == null) tc = 0;
                        else if (q.pitch == null || st.pitch == null) tc = restTrans;
                        else tc = jumpCost * Math.abs(st.pitch - q.pitch);
                        var cc = q.cost + tc;
                        if (cc < bestCost) { bestCost = cc; bestBack = qi; }
                    }
                }
                cur.push({ pitch: st.pitch, cost: bestCost + st.emit });
                back.push(bestBack);
            }
            trellis[f] = { states: cur, back: back };
            prev = cur;
        }
        var fin = trellis[nF - 1].states, best = Infinity, idx = 0;
        for (var si2 = 0; si2 < fin.length; si2++) if (fin[si2].cost < best) { best = fin[si2].cost; idx = si2; }
        var seq = new Array(nF);
        for (var f3 = nF - 1; f3 >= 0; f3--) {
            seq[f3] = trellis[f3].states[idx].pitch;
            idx = trellis[f3].back[idx];
            if (idx < 0 && f3 > 0) idx = 0;
        }
        var out = [], curNote = null;
        for (var f4 = 0; f4 < nF; f4++) {
            var pp = seq[f4];
            if (pp == null) { if (curNote) { out.push(curNote); curNote = null; } continue; }
            if (curNote && curNote.pitchMidi === pp) curNote.durationSeconds = (f4 + 1) * hop + t0 - curNote.startTimeSeconds;
            else { if (curNote) out.push(curNote); curNote = { pitchMidi: pp, startTimeSeconds: t0 + f4 * hop, durationSeconds: hop, amplitude: 0.75 }; }
        }
        if (curNote) out.push(curNote);
        return cleanNotes(out, { minDurSec: minDur, mergeGapSec: 0.08, minAmpFrac: 0, monophonic: false });
    }

    // Intrinsic melody-likeness of a decoded line: salient-coverage (Σ best
    // original amplitude at the line's pitch × duration / span) — rewards
    // following genuinely-loud, present notes — so a quiet pad loses; but a
    // loud SUSTAINED counter-line would tie, so multiply by activity (note
    // count)^P, since a melody MOVES while a drone does not.
    function scoreLine(line, notes, span, activityPow){
        if (!line.length) return 0;
        var sal = 0;
        for (var i = 0; i < line.length; i++) {
            var ln = line[i], ls = ln.startTimeSeconds, le = ls + ln.durationSeconds, amp = 0;
            for (var j = 0; j < notes.length; j++) {
                var o = notes[j];
                if (o.pitchMidi !== ln.pitchMidi) continue;
                var ov = Math.min(le, o.startTimeSeconds + (o.durationSeconds || 0)) - Math.max(ls, o.startTimeSeconds);
                if (ov > 0 && (o.amplitude || 0) > amp) amp = o.amplitude || 0;
            }
            sal += amp * ln.durationSeconds;
        }
        return (sal / (span || 1)) * Math.pow(line.length, activityPow);
    }

    function extractMelody(notes, opts){
        opts = opts || {};
        if (!notes || notes.length < 3) return notes ? notes.slice() : [];
        var hop       = opts.hopSec     != null ? opts.hopSec     : 0.05;
        var jumpCost  = opts.jumpCost   != null ? opts.jumpCost   : 0.05;  // per semitone
        var restCost  = opts.restCost   != null ? opts.restCost   : 0.55;  // REST emission
        var restTrans = opts.restTrans  != null ? opts.restTrans  : 0.35;  // enter/leave REST
        var sigma     = opts.sigma      != null ? opts.sigma      : 9;      // register-prior width (semitones)
        var minDur    = opts.minDurSec  != null ? opts.minDurSec  : 0.09;
        var activityPow = opts.activityPow != null ? opts.activityPow : 0.35;

        var t0 = Infinity, t1 = -Infinity;
        for (var i = 0; i < notes.length; i++) {
            var s = notes[i].startTimeSeconds || 0, e = s + (notes[i].durationSeconds || 0);
            if (s < t0) t0 = s;
            if (e > t1) t1 = e;
        }
        if (!(t1 > t0)) return notes.slice();
        var nF = Math.max(1, Math.ceil((t1 - t0) / hop)), span = t1 - t0;

        // Per-frame candidate notes (a note covers every frame it sounds in).
        var cand = new Array(nF);
        for (var f = 0; f < nF; f++) cand[f] = [];
        for (var i2 = 0; i2 < notes.length; i2++) {
            var n = notes[i2], ns = n.startTimeSeconds || 0, ne = ns + (n.durationSeconds || 0);
            var fa = Math.max(0, Math.floor((ns - t0) / hop));
            var fb = Math.min(nF - 1, Math.floor((Math.max(ns, ne - 1e-6) - t0) / hop));
            for (var ff = fa; ff <= fb; ff++) cand[ff].push(n);
        }

        // Single forced centre (tests / overrides) vs. multi-hypothesis sweep.
        if (opts.centerMidi != null) {
            return viterbiMelody(cand, nF, hop, t0, opts.centerMidi, opts.sigma != null ? opts.sigma : 11, jumpCost, restCost, restTrans, minDur);
        }
        var centers = opts.centers || [52, 56, 60, 64, 68, 72, 76, 80]; // ~E3..G♯5
        var bestLine = null, bestScore = -1;
        for (var ci = 0; ci < centers.length; ci++) {
            var line = viterbiMelody(cand, nF, hop, t0, centers[ci], sigma, jumpCost, restCost, restTrans, minDur);
            var sc = scoreLine(line, notes, span, activityPow);
            if (sc > bestScore) { bestScore = sc; bestLine = line; }
        }
        return bestLine || [];
    }

    // ─── Parts of a choir / vocal ensemble ──────────────────────────────────
    // Basic Pitch hears a choir well — frame multi-pitch F 0.73–0.85 on the Choral
    // Singing Dataset (CC BY 4.0, 3 pieces, scores aligned to the singing) — but it
    // returns ONE pool of notes. The melody view then follows the loudest middle
    // line: on SATB it covered the ALTO 40–74 % of the time and the soprano 3–23 %
    // (bug report 16.10.2026: «хор, сопрано 1 начало с ля, сопрано 2 с ми…»).
    //
    // splitVoices deals the pool out to k parts, top voice first. Choir parts keep
    // their order (a soprano does not cross the bass), so each 20 ms frame's
    // pitches are matched to the parts IN ORDER — an order-preserving assignment
    // (dynamic programming over pitches × parts) whose cost is the distance from
    // each part's last pitch; a pitch nobody takes costs its relative loudness, a
    // part left silent costs a constant, a silent part drifts back to its register.
    // A Basic Pitch note then goes to the part that held it on most of its frames,
    // and each part is made monophonic.
    //
    // Measured against the per-part scores (frame F per part, ±150 ms because the
    // scores are only semi-synchronised to the singing), CSD, Basic Pitch notes:
    //   2 parts (S+A)        0.81–0.88      3 parts (S+A+T)   0.70–0.80
    //   SATB (16 singers)    0.63–0.73      soloist over ATB  0.45–0.62
    // Rejected by the same numbers (mean F per part, 15 mixes): rank by pitch in
    // each frame 0.559 (one missed note shifts every part below it); peeling
    // Viterbi lines from the top 0.536 (the bass ends up with leftovers).
    function voiceFrames(notes, hop, nF){
        var fr = new Array(nF), i, f;
        for (f = 0; f < nF; f++) fr[f] = null;
        for (i = 0; i < notes.length; i++) {
            var n = notes[i], s = n.startTimeSeconds || 0, e = s + (n.durationSeconds || 0);
            var fa = Math.floor(s / hop), fb = Math.max(fa + 1, Math.floor(e / hop));
            for (f = Math.max(0, fa); f < Math.min(nF, fb); f++) {
                var m = fr[f] || (fr[f] = {}), p = n.pitchMidi, a = n.amplitude || 0;
                if (!m[p] || a > m[p].a) m[p] = { a: a, i: i };
            }
        }
        var out = new Array(nF);
        for (f = 0; f < nF; f++) {
            var lst = [];
            if (fr[f]) for (var k in fr[f]) lst.push({ p: +k, a: fr[f][k].a, i: fr[f][k].i });
            lst.sort(function(x, y){ return y.p - x.p; });
            out[f] = lst;
        }
        return out;
    }
    function splitVoices(notes, k, opts){
        opts = opts || {};
        k = Math.max(1, k | 0);
        var hop = opts.hopSec || 0.02;
        var dropCost = opts.dropCost != null ? opts.dropCost : 6;
        var idleCost = opts.idleCost != null ? opts.idleCost : 4;
        var decay = opts.decay != null ? opts.decay : 0.98;
        var parts = [], v;
        for (v = 0; v < k; v++) parts.push([]);
        if (!notes || !notes.length) return parts;
        var end = 0, i;
        for (i = 0; i < notes.length; i++) end = Math.max(end, (notes[i].startTimeSeconds || 0) + (notes[i].durationSeconds || 0));
        var nF = Math.ceil(end / hop) + 2;
        var fr = voiceFrames(notes, hop, nF);
        // Each part starts at its share of the pitch distribution, top part highest.
        var ps = notes.map(function(n){ return n.pitchMidi; }).sort(function(a, b){ return a - b; });
        var centre = [], ref = [];
        for (v = 0; v < k; v++) {
            var q = ps[Math.min(ps.length - 1, Math.floor(ps.length * (1 - (v + 0.5) / k)))];
            centre.push(q); ref.push(q);
        }
        var votes = {};
        var INF = 1e18;
        for (var f = 0; f < nF; f++) {
            var lst = fr[f], n = lst.length;
            if (!n) { for (v = 0; v < k; v++) ref[v] = decay * ref[v] + (1 - decay) * centre[v]; continue; }
            var amax = 0;
            for (i = 0; i < n; i++) if (lst[i].a > amax) amax = lst[i].a;
            amax = amax || 1;
            // D[i][j]: cheapest way to place the first i pitches on the first j parts.
            var D = [], B = [], a, b;
            for (a = 0; a <= n; a++) { D.push(new Array(k + 1).fill(INF)); B.push(new Array(k + 1).fill(0)); }
            D[0][0] = 0;
            for (a = 0; a <= n; a++) for (b = 0; b <= k; b++) {
                var c = D[a][b];
                if (c >= INF) continue;
                if (a < n && b < k) { var cm = c + Math.abs(lst[a].p - ref[b]); if (cm < D[a + 1][b + 1]) { D[a + 1][b + 1] = cm; B[a + 1][b + 1] = 1; } }
                if (a < n) { var cd = c + dropCost * (lst[a].a / amax); if (cd < D[a + 1][b]) { D[a + 1][b] = cd; B[a + 1][b] = 2; } }
                if (b < k) { var ci = c + idleCost; if (ci < D[a][b + 1]) { D[a][b + 1] = ci; B[a][b + 1] = 3; } }
            }
            var used = new Array(k).fill(false);
            a = n; b = k;
            while (a > 0 || b > 0) {
                var op = B[a][b];
                if (op === 1) {
                    var it = lst[a - 1];
                    (votes[it.i] || (votes[it.i] = new Array(k).fill(0)))[b - 1]++;
                    ref[b - 1] = it.p; used[b - 1] = true;
                    a--; b--;
                } else if (op === 2) a--;
                else b--;
            }
            for (v = 0; v < k; v++) if (!used[v]) ref[v] = decay * ref[v] + (1 - decay) * centre[v];
        }
        // A note goes where it was held most; each part is one line.
        for (var idx in votes) {
            var vs = votes[idx], best = 0;
            for (v = 1; v < k; v++) if (vs[v] > vs[best]) best = v;
            if (!vs[best]) continue;
            var src = notes[+idx];
            parts[best].push({ pitchMidi: src.pitchMidi, startTimeSeconds: src.startTimeSeconds, durationSeconds: src.durationSeconds,
                amplitude: src.amplitude, part: best });
        }
        for (v = 0; v < k; v++) {
            var line = parts[v].sort(function(x, y){ return x.startTimeSeconds - y.startTimeSeconds; }), mono = [];
            for (i = 0; i < line.length; i++) {
                var nt = line[i], last = mono[mono.length - 1];
                if (last && nt.startTimeSeconds < last.startTimeSeconds + last.durationSeconds) {
                    if (nt.startTimeSeconds - last.startTimeSeconds < 0.06) {
                        if (nt.durationSeconds > last.durationSeconds) mono[mono.length - 1] = nt;
                        continue;
                    }
                    last.durationSeconds = nt.startTimeSeconds - last.startTimeSeconds;
                }
                mono.push(nt);
            }
            parts[v] = mono.filter(function(x){ return x.durationSeconds >= 0.05; });
        }
        return parts;
    }
    // Frames of a multi-pitch network (the choir network, public/js/choir-parts) → notes:
    // a pitch, rounded to the semitone, held over consecutive frames is one note; notes
    // under 50 ms are dropped. splitVoices then deals them out like Basic Pitch's notes.
    // Measured 2026-10-18 on Cantoría (42 recordings, per-part F against CREPE truth):
    // this 0.861 frame / 0.739 note; the same frames split frame by frame and cut into
    // notes by the singing-voice segmenter (neuralFramesToNotes) 0.831 / 0.616; a
    // monophonic clean-up of each part afterwards 0.851 / 0.630 (it glues re-sung notes).
    function framesToRunNotes(frames, hopS, minDurS){
        minDurS = minDurS != null ? minDurS : 0.05;
        var open = {}, out = [], T = frames ? frames.length : 0, t, p;
        for (t = 0; t <= T; t++) {
            var cur = {};
            if (t < T) for (var i = 0; i < frames[t].length; i++) {
                var f = frames[t][i], r = Math.round(f.m);
                if (!cur[r] || f.s > cur[r]) cur[r] = f.s;
            }
            for (p in open) if (!cur[p]) {
                var o = open[p], d = (t - o.t0) * hopS;
                if (d >= minDurS) out.push({ pitchMidi: +p, startTimeSeconds: o.t0 * hopS, durationSeconds: d, amplitude: o.s / o.n });
                delete open[p];
            }
            for (p in cur) {
                if (open[p]) { open[p].s += cur[p]; open[p].n++; }
                else open[p] = { t0: t, s: cur[p], n: 1 };
            }
        }
        out.sort(function(x, y){ return x.startTimeSeconds - y.startTimeSeconds || y.pitchMidi - x.pitchMidi; });
        return out;
    }
    // How many parts sound at once: the 75th percentile of distinct pitches per
    // sounding frame. Measured on CSD: right on all 2- and 3-part mixes; on 16-
    // singer SATB it says 3 one time in two — the person can change the count.
    function estimateVoices(notes, opts){
        opts = opts || {};
        var hop = opts.hopSec || 0.02;
        if (!notes || !notes.length) return 1;
        var end = 0;
        for (var i = 0; i < notes.length; i++) end = Math.max(end, (notes[i].startTimeSeconds || 0) + (notes[i].durationSeconds || 0));
        var fr = voiceFrames(notes, hop, Math.ceil(end / hop) + 2), cnt = [];
        for (var f = 0; f < fr.length; f++) if (fr[f].length) cnt.push(fr[f].length);
        if (!cnt.length) return 1;
        cnt.sort(function(a, b){ return a - b; });
        var pos = 0.75 * (cnt.length - 1), lo = Math.floor(pos);
        var qv = cnt[lo] + (cnt[Math.min(cnt.length - 1, lo + 1)] - cnt[lo]) * (pos - lo);
        return Math.max(1, Math.floor(qv + 1e-9));
    }

    // `self`, not `window`: identical in a document (self === window there), but
    // it also makes this module importScripts-able from a worker. The chiptune
    // render worker needs extractMelody to voice notes off the main thread, and
    // a bare `window.` assignment throws on load in worker scope — the module
    // would look "loaded" and be silently absent.
    self.__atnClean = {
        polyphony: polyphony,
        melodicMedianJump: melodicMedianJump,
        lineRegisterGap: lineRegisterGap,
        voicesBelowLine: voicesBelowLine,
        voicesAboveLine: voicesAboveLine,
        cleanNotes: cleanNotes,
        segmentVocalFrames: segmentVocalFrames,
        segmentNeuralFrames: segmentNeuralFrames,
        neuralFramesToNotes: neuralFramesToNotes,
        yinSegmentsToNotes: yinSegmentsToNotes,
        extractMelody: extractMelody,
        splitVoices: splitVoices,
        framesToRunNotes: framesToRunNotes,
        estimateVoices: estimateVoices,
        midiFromHz: midiFromHz
    };
})();
